import { useEffect, useState } from "react";
import {
  UserPlus, ShoppingCart, CreditCard, FileText, Package,
  AlertCircle, Bell, TrendingUp, Users, LucideIcon,
} from "lucide-react";
import { cn, formatEuro } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { useWorkspace } from "@/hooks/useWorkspace";

interface Activity {
  id: string;
  type: string;
  icon: LucideIcon;
  title: string;
  description: string;
  time: string;
  status: "success" | "warning" | "danger" | "info" | "neutral";
}

const statusColors = {
  success: "bg-success/20 text-success",
  warning: "bg-warning/20 text-warning",
  danger: "bg-destructive/20 text-destructive",
  info: "bg-info/20 text-info",
  neutral: "bg-muted text-muted-foreground",
};

const typeIcons: Record<string, LucideIcon> = {
  client: UserPlus, order: ShoppingCart, payment: CreditCard,
  invoice: FileText, product: Package, opportunity: TrendingUp,
  contact: Users, notification: Bell, default: AlertCircle,
};

const typeStatus: Record<string, "success" | "warning" | "danger" | "info" | "neutral"> = {
  client: "success", order: "info", payment: "success", invoice: "neutral",
  product: "info", opportunity: "info", contact: "success", notification: "neutral",
};

export function RecentActivity() {
  const { user } = useAuth();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id || null;
  const userId = user?.id;

  useEffect(() => {
    if (userId && workspaceId) fetchRecentActivity();
  }, [userId, workspaceId]);

  const fetchRecentActivity = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const allActivities: Activity[] = [];

      const { data: notifications } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user?.id)
        .order("created_at", { ascending: false })
        .limit(10);

      notifications?.forEach((notification) => {
        let activityType = "notification";
        if (notification.title.toLowerCase().includes("cliente")) activityType = "client";
        else if (notification.title.toLowerCase().includes("orden") || notification.title.toLowerCase().includes("pedido")) activityType = "order";
        else if (notification.title.toLowerCase().includes("pago")) activityType = "payment";
        else if (notification.title.toLowerCase().includes("factura")) activityType = "invoice";
        else if (notification.title.toLowerCase().includes("contacto")) activityType = "contact";
        else if (notification.title.toLowerCase().includes("oportunidad")) activityType = "opportunity";

        allActivities.push({
          id: `notification-${notification.id}`,
          type: activityType,
          icon: typeIcons[activityType] || typeIcons.default,
          title: notification.title,
          description: notification.message,
          time: formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: es }),
          status: notification.type === "success" ? "success" : notification.type === "warning" ? "warning" : notification.type === "error" ? "danger" : "info",
        });
      });

      const { data: payments } = await supabase
        .from("payments")
        .select("id, amount, payment_date, payment_number, clients(name)")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false })
        .limit(5);

      payments?.forEach((payment) => {
        const clientName = (payment.clients as any)?.name || "Cliente";
        allActivities.push({
          id: `payment-${payment.id}`, type: "payment", icon: CreditCard,
          title: "Pago procesado",
          description: `Pago de ${formatEuro(Number(payment.amount))} recibido de ${clientName}`,
          time: payment.payment_date ? formatDistanceToNow(new Date(payment.payment_date), { addSuffix: true, locale: es }) : "Recientemente",
          status: "success",
        });
      });

      const { data: orders } = await supabase
        .from("orders")
        .select("id, order_number, total, created_at, clients(name)")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false })
        .limit(5);

      orders?.forEach((order) => {
        const clientName = (order.clients as any)?.name || "Cliente";
        allActivities.push({
          id: `order-${order.id}`, type: "order", icon: ShoppingCart,
          title: "Nueva orden recibida",
          description: `Orden ${order.order_number} - Cliente: ${clientName}`,
          time: formatDistanceToNow(new Date(order.created_at), { addSuffix: true, locale: es }),
          status: "info",
        });
      });

      const { data: invoices } = await supabase
        .from("invoices")
        .select("id, invoice_number, total, status, created_at, clients(name)")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false })
        .limit(5);

      invoices?.forEach((invoice) => {
        const clientName = (invoice.clients as any)?.name || "Cliente";
        const isOverdue = invoice.status === "overdue";
        allActivities.push({
          id: `invoice-${invoice.id}`, type: "invoice",
          icon: isOverdue ? AlertCircle : FileText,
          title: isOverdue ? "Factura vencida" : "Factura generada",
          description: `Factura ${invoice.invoice_number} - ${clientName} - ${formatEuro(Number(invoice.total))}`,
          time: formatDistanceToNow(new Date(invoice.created_at), { addSuffix: true, locale: es }),
          status: isOverdue ? "danger" : "neutral",
        });
      });

      setActivities(allActivities.slice(0, 6));
    } catch (error) {
      console.error("Error fetching recent activity:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="chart-container animate-slide-in-up" style={{ animationDelay: "300ms" }}>
        <div className="mb-6">
          <h3 className="font-display text-lg font-semibold text-foreground">Actividad Reciente</h3>
          <p className="text-sm text-muted-foreground">Últimos eventos del sistema</p>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex items-start gap-4 p-3">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <div className="flex-1 space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-3 w-1/2" /></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container animate-slide-in-up" style={{ animationDelay: "300ms" }}>
      <div className="mb-6">
        <h3 className="font-display text-lg font-semibold text-foreground">Actividad Reciente</h3>
        <p className="text-sm text-muted-foreground">Últimos eventos del sistema</p>
      </div>
      <div className="space-y-4">
        {activities.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No hay actividad reciente</div>
        ) : (
          activities.map((activity, index) => (
            <div key={activity.id} className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors animate-slide-in-left" style={{ animationDelay: `${400 + index * 50}ms` }}>
              <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", statusColors[activity.status])}>
                <activity.icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{activity.title}</p>
                <p className="text-sm text-muted-foreground truncate">{activity.description}</p>
                <p className="text-xs text-muted-foreground mt-1">{activity.time}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
